<template>
  <section>
    <div class="content-wrapper">
      <div class="content-heading">
        Micro Deposits
        <small data-localize="dashboard.WELCOME"></small>
      </div>

      <div class="row">
        <div class="col-lg-12">
          <div v-if="instructions.length > 0" class="alert alert-warning" role="alert">
            <h4><em class="icon-exclamation pr-sm"></em>Please take the following steps to retry bank account verification via micro deposit:</h4>
            <strong>
              <ol>
                <li v-for="instruction in instructions">{{ instruction }}</li>
              </ol>
            </strong>
          </div>
          <div id="panelChart9" class="panel panel-default panel-height">

            <div class="accounts-title">
              <div class="panel-title">
                Micro Deposits - Verifications
              </div>
            </div>
            <p class="top-margin">Please input the two micro-deposited amounts that we have made on your bank account: </p>
            <form role="form" class="mb-lg top-margin" v-on:submit.prevent="submitVerifyMicroDeposit">
              <div class="row">
                <div class="col-md-2">
                  <div class="form-group">
                    <label for="amount1" class="control-label">Amount 1</label>
                    <input step="0.0001" v-model="microDeposit.requestBody.amount1.value" type="number" class="form-control" id="amount1" placeholder="Micro Deposit - Amount 1" required>
                  </div>
                </div>
                <div class="col-md-2">
                  <div class="form-group">
                    <label for="amount2" class="control-label">Amount 2</label>
                    <input step="0.0001" v-model="microDeposit.requestBody.amount2.value" type="number" class="form-control" id="amount2" placeholder="Micro Deposit - Amount 2" required>
                  </div>
                </div>
              </div>
              <div class="row" v-if="verifyError.length !== 0 || amountError.length !== 0">
                <div class="col-md-4">
                  <strong><p class="text-red">{{ verifyError }}</p></strong>
                  <strong><p class="text-red">{{ amountError }}</p></strong>
                </div>
              </div>

              <div class="row">
                <div class="col-md-2">
                  <button type="submit" v-bind:disabled="microDeposit.fundingSourceId.length === 0 || maxAttempsReached" class="btn btn-info btn-lg button-submit">Submit</button>
                </div>
              </div>
            </form>

          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
  import {
    mapState,
    mapActions,
    mapMutations,
  } from 'vuex';
  import {
    HIDE_QUESTION_MODAL,
    SET_USER,
  } from '@/store/mutation-types';

  export default {
    name: 'microDepositsVerification',
    beforeMount() {
      this.loadUserProfile();
      this.hideQuestionModal();
      this.setFundingSourceId();
      this.setActiveAccountIndex();
    },
    data() {
      return {
        maxAttempsReached: false,
        instructions: '',
        verifyError: '',
        amountError: '',
        microDeposit: {
          fundingSourceId: '',
          requestBody: {
            amount1: {
              value: '0.0',
              currency: 'USD',
            },
            amount2: {
              value: '0.0',
              currency: 'USD',
            },
          },
        },
      };
    },
    computed: {
      ...mapState('app', [
        'user',
        'activeAccountId',
      ]),
    },
    methods: {
      ...mapActions('accountCreation', [
        'getUserProfile',
      ]),
      ...mapActions('account', [
        'setDefaultFundingSource',
        'getAccountFundingSources',
      ]),
      ...mapMutations('question', {
        hideQuestionModal: HIDE_QUESTION_MODAL,
      }),
      ...mapMutations('app', {
        setUser: SET_USER,
      }),
      ...mapActions('microDepositsVerification', [
        'verifyMicroDeposit',
      ]),
      loadUserProfile() {
        return this.getUserProfile().then((data) => {
          this.setUser(data);
          this.previousFundingSourceId = data.accounts[0].defaultFundingSourceId;
        });
      },
      setFundingSourceId() {
        this.microDeposit.fundingSourceId = this.$route.params.id;
      },
      setActiveAccountIndex() {
        const activeAccount = this.user.accounts.find(ac => ac.id === this.activeAccountId);
        this.activeAccountIndex = this.user.accounts.indexOf(activeAccount);
      },
      validateAmount(amount) {
        if (amount >= 0.1) {
          this.amountError = 'Micro-deposits amount must be less than 10 cents';
          return false;
        }
        return true;
      },
      validateAmounts() {
        const firstAmount = this.microDeposit.requestBody.amount1.value;
        const secondAmount = this.microDeposit.requestBody.amount2.value;
        return this.validateAmount(firstAmount) && this.validateAmount(secondAmount);
      },
      showErrors(message) {
        const finalError = message.includes('INVALID_MAX_ATTEMPTS');
        if (!finalError) {
          this.verifyError = 'Amounts introduced are incorrect, please try again!';
        }
        if (finalError) {
          this.instructions = ['Removal of the bank account with failed micro-deposit verification attempts.', 'Wait 48 hours after the initial bank account was added to re-add the bank account.', 'Initiate new micro-deposits to the bank account.', 'Verify the bank account using the new posted micro-deposit amounts.'];
          this.verifyError = 'You have reached the maximum (3) of attempts!';
          this.maxAttempsReached = true;
        }
      },
      showInvestmentView() {
        this.$router.replace({ name: 'AddFundingSource', params: { addedFundingSource: true } });
      },
      resetErrors() {
        this.amountError = '';
        this.verifyError = '';
      },
      submitVerifyMicroDeposit() {
        const {
          microDeposit,
        } = this;
        this.resetErrors();
        const validAmounts = this.validateAmounts();
        if (!validAmounts) {
          return;
        }
        this.verifyMicroDeposit({
          microDeposit,
        }).then((resp) => {
          // Some API implementations return a resolved response with an error message.
          if (resp && resp.message) {
            this.showErrors(resp.message);
            return;
          }
          this.getAccountFundingSources(this.activeAccountId).then(() => {
            this.loadUserProfile().then(() => {
              if (!this.previousFundingSourceId) {
                const account = this.user.accounts[0];
                const fundingSourceId = this.microDeposit.fundingSourceId;
                this.setDefaultFundingSource({
                  accountId: account.id,
                  fundingSourceId,
                }).then(() => {
                  this.previousFundingSourceId = fundingSourceId;
                  this.user.accounts[0].defaultFundingSourceId = fundingSourceId;
                  this.showInvestmentView();
                });
              } else {
                this.showInvestmentView();
              }
            });
          });
        }).catch((err) => {
          this.showErrors(err.message);
        });
      },
    },
  };
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
  .button-submit {
    font-size: 14px;
    display: block;
    margin: 0% 4% 3%;
    color: #FFFFFF;
    background-color: #127285;
  }

  .top-margin {
    margin-top: 3%;
  }

  .left-margin {
    margin-left: 3%;
  }

  .text-red {
    color: #a94442;
  }
</style>



// WEBPACK FOOTER //
// MicroDepositsVerification.vue?21475a52